import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import MuiLanIcon from '@mui/icons-material/Lan';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';
import MuiDescriptionIcon from '@mui/icons-material/Description';

import type { StarPrinterStatus } from 'src/services/printers';

import MuiIcon from 'src/components/mui-icon';

import { lanPrinterTitle } from './lan-address';
import type { Candidate } from './use-lan-printer-setup';

// ----------------------------------------------------------------------

interface LanCandidatePanelProps {
  candidate: Candidate;
  saving: boolean;
  onTestPrint: () => void;
  onSave: () => void;
  onBack: () => void;
}

type ChipColor = 'success' | 'error' | 'warning' | 'default';

function statusChips(status: StarPrinterStatus | undefined): { label: string; color: ChipColor }[] {
  if (!status) return [];
  // A printer that never described itself is reported as exactly that: the
  // desktop backend then sends a `message` and omits the detail flags.
  if (status.message !== undefined && status.cutterError === undefined) {
    return [{ label: 'Paper and cover status not reported', color: 'default' }];
  }
  const chips: { label: string; color: ChipColor }[] = [
    status.online ? { label: 'Online', color: 'success' } : { label: 'Offline', color: 'error' },
  ];
  if (status.coverOpen) chips.push({ label: 'Cover open', color: 'warning' });
  if (status.paperEmpty) chips.push({ label: 'No paper', color: 'error' });
  else if (status.paperNearEmpty) chips.push({ label: 'Low paper', color: 'warning' });
  return chips;
}

export default function LanCandidatePanel({
  candidate,
  saving,
  onTestPrint,
  onSave,
  onBack,
}: LanCandidatePanelProps) {
  const { printer, check, test } = candidate;
  const port = printer.connection.interfaceType === 'lan' ? printer.connection.port : undefined;
  const busy = check === 'checking' || test === 'printing' || saving;
  const tested = test === 'printed';
  const chips = statusChips(candidate.status);

  return (
    <Stack spacing={2} sx={{ px: 3, pb: 1 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ bgcolor: 'primary.lighter', color: 'primary.main', width: 48, height: 48 }}>
          <MuiIcon icon={MuiLanIcon} width={24} />
        </Avatar>
        <Box>
          <Typography variant="subtitle1">{lanPrinterTitle(printer)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {printer.ipAddress}
            {port !== undefined ? ` · port ${String(port)}` : ''}
          </Typography>
        </Box>
      </Stack>

      {check === 'checking' && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Connecting to {printer.ipAddress}…</Typography>
        </Stack>
      )}

      {check === 'reachable' && (
        <Alert severity="success">
          <Typography variant="body2" sx={{ mb: chips.length ? 1 : 0 }}>
            The printer answered.
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {chips.map((chip) => (
              <Chip
                key={chip.label}
                label={chip.label}
                color={chip.color}
                size="small"
                variant="soft"
              />
            ))}
          </Stack>
        </Alert>
      )}

      {check === 'unreachable' && (
        <Alert severity="error">
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {candidate.checkError}
          </Typography>
          <Typography variant="caption" component="div">
            Check the printer is on, its network light is lit, and it is on the same network as this
            register. The address is on the printer’s self-test page.
          </Typography>
        </Alert>
      )}

      {check !== 'checking' && (
        <Box>
          <LoadingButton
            variant={tested ? 'outlined' : 'contained'}
            loading={test === 'printing'}
            disabled={busy || check === 'unreachable'}
            onClick={onTestPrint}
            startIcon={<MuiIcon icon={MuiDescriptionIcon} />}
          >
            {tested ? 'Print another test page' : 'Print test page'}
          </LoadingButton>
          {tested && (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              Test page sent. If it came out of the printer, save it as this register’s printer.
            </Alert>
          )}
          {test === 'failed' && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {candidate.testError}
            </Alert>
          )}
        </Box>
      )}

      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        sx={{ pt: 1 }}
      >
        <Button onClick={onBack} disabled={saving}>
          Back
        </Button>
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1}>
          {!tested && check !== 'checking' && (
            // The explicit override: saving without a successful test page.
            <Button color="inherit" disabled={busy} onClick={onSave}>
              {check === 'unreachable' ? 'Save anyway' : 'Save without a test page'}
            </Button>
          )}
          <LoadingButton
            variant="contained"
            loading={saving}
            disabled={!tested || busy}
            onClick={onSave}
          >
            Save printer
          </LoadingButton>
        </Stack>
      </Stack>
    </Stack>
  );
}
